import React, { useEffect, useState } from 'react';
import { interactionsApi } from '../../api/interactionsApi';

// How long a 'pending'/'summarizing' row can sit before Retry appears for it
// too, not just for 'failed' -- the summarization dispatch (Celery, with a
// BackgroundTasks fallback when no worker is reachable -- see
// pipeline_dispatcher.dispatch_summarization_pipeline) should normally
// finish in well under this window, so a row still stuck past it is worth
// a manual nudge rather than a silent dead end.
const STUCK_THRESHOLD_MS = 2 * 60 * 1000;

function isStuck(item) {
  if (item.summarization_status !== 'pending' && item.summarization_status !== 'summarizing') {
    return false;
  }
  const createdAt = item.created_at ? new Date(item.created_at).getTime() : NaN;
  return Number.isFinite(createdAt) && Date.now() - createdAt > STUCK_THRESHOLD_MS;
}

const InteractionsTab = ({ projectId, openUploadDialog }) => {
  const [interactions, setInteractions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchInteractions = async () => {
    try {
      setLoading(true);
      const data = await interactionsApi.getInteractions(projectId);
      setInteractions(data);
      setError(null);
    } catch (err) {
      setError('Failed to load interactions.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInteractions();
  }, [projectId]);

  // Poll while any interaction is still being summarized — stop once all are settled
  useEffect(() => {
    const hasPending = interactions.some(
      (i) => i.summarization_status === 'pending' || i.summarization_status === 'summarizing'
    );
    if (!hasPending) return;
    const interval = setInterval(fetchInteractions, 10000);
    return () => clearInterval(interval);
  }, [interactions]);

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this interaction context?')) return;
    try {
      await interactionsApi.deleteInteraction(projectId, id);
      setInteractions(interactions.filter(i => i.id !== id));
    } catch (err) {
      alert('Failed to delete interaction.');
    }
  };

  const handleResummarize = async (id) => {
    try {
      await interactionsApi.summarizeInteraction(projectId, id);
      fetchInteractions();
    } catch (err) {
      alert('Failed to trigger resummarization.');
    }
  };

  if (loading && interactions.length === 0) return <div className="p-4 text-gray-500">Loading context...</div>;
  if (error) return <div className="p-4 text-red-500">{error}</div>;

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-800">Project Interactions Context</h2>
          <p className="text-sm text-gray-500">
            Upload meeting notes or paste emails to give the AI more context for generating requirements.
          </p>
        </div>
        <button
          onClick={openUploadDialog}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 shadow-sm"
        >
          Meeting Notes
        </button>
      </div>

      {interactions.length === 0 ? (
        <div className="bg-gray-50 border border-dashed border-gray-300 rounded-lg p-8 text-center">
          <p className="text-gray-500 mb-4">No interaction context added yet.</p>
          <button
            onClick={openUploadDialog}
            className="text-blue-600 font-medium hover:underline"
          >
            Click here to upload or paste notes.
          </button>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white rounded-lg overflow-hidden border">
            <thead className="bg-gray-50">
              <tr>
                <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Source Date</th>
                <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="py-3 px-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {interactions.map(item => (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="py-4 px-4 text-sm text-gray-900 font-medium">
                    {item.title}
                    {item.filename && <div className="text-xs text-gray-500 mt-1">{item.filename}</div>}
                  </td>
                  <td className="py-4 px-4 text-sm text-gray-500 capitalize">
                    {item.interaction_type ? item.interaction_type.replace(/_/g, ' ') : '-'}
                  </td>
                  <td className="py-4 px-4 text-sm text-gray-500">{item.source_date || '-'}</td>
                  <td className="py-4 px-4 text-sm">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                      ${item.summarization_status === 'completed' ? 'bg-green-100 text-green-800' : 
                        item.summarization_status === 'failed' ? 'bg-red-100 text-red-800' : 
                        'bg-yellow-100 text-yellow-800'}`}>
                      {item.summarization_status}
                    </span>
                  </td>
                  <td className="py-4 px-4 text-sm font-medium text-right space-x-3">
                    {(item.summarization_status === 'failed' || isStuck(item)) && (
                      <button onClick={() => handleResummarize(item.id)} className="text-blue-600 hover:text-blue-900">
                        Retry
                      </button>
                    )}
                    <button onClick={() => handleDelete(item.id)} className="text-red-600 hover:text-red-900">
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default InteractionsTab;
